import { CloseCircleFilled, DeleteOutlined, EditOutlined, PlusOutlined, UploadOutlined } from "@ant-design/icons";
import { DatePicker, Form, Input, message, Modal, Popconfirm, Select } from "antd";
import type { TableColumnsType } from "antd";
import { departmentApi, employeeApi } from "api/employee.api";
import { AppButton, AppTable, AppTableToolbar } from "components/antd-ui";
import { ImportExcelModal, ImportExcelResult } from "components/Import/ImportExcelModal";
import { InputNumber } from "components/Input/InputNumber";
import { getTableShowingRange, StatusCell, TableContent, TableLayout, TableLayoutFooter } from "components/table";
import { fetchStaffOptions, RemoteOption, useRemoteOptions } from "hooks/useRemoteOptions";
import { useSearchInput } from "hooks/useSearchInput";
import { observer } from "mobx-react";
import { useCallback, useEffect, useMemo, useState } from "react";
import { userStore } from "store/userStore";
import {
  Department,
  Employee,
  EmployeeStatus,
  employeeStatusOptions,
  employeeStatusTrans,
  TechnicalCommissionRole,
  technicalCommissionRoleOptions,
} from "types/employee";
import { formatVND, getTitle } from "utils";
import { can } from "utils/auth";
import dayjs, { Dayjs } from "utils/dayjs";
import "./Employee.scss";

const employeePath = "/employee";
const requiredRules = [{ required: true, message: "Bắt buộc" }];

const formatMoney = (num?: number) => `${formatVND(Math.round(num || 0))}đ`;
const toDate = (value?: string) => (value ? dayjs(value) : undefined);
const fromDate = (value?: Dayjs | null) => (value ? value.format("YYYY-MM-DD") : "");
const displayDate = (value?: string) => (value ? dayjs(value).format("DD/MM/YYYY") : "--");

interface EmployeeForm {
  name: string;
  phone?: string;
  email?: string;
  departmentId?: number;
  staffId?: number;
  status: EmployeeStatus;
  technicalCommissionRole?: TechnicalCommissionRole;
  probationStartDate?: Dayjs;
  officialDate?: Dayjs;
  resignedDate?: Dayjs;
  baseSalary?: number | string;
  lastReviewSalary?: number | string;
  lastReviewDate?: Dayjs;
  nextReviewDate?: Dayjs;
  idNumber?: string;
  bankName?: string;
  bankAccount?: string;
}

export const EmployeePage = observer(({ title = "" }) => {
  const [form] = Form.useForm<EmployeeForm>();
  const statusValue = Form.useWatch("status", form);

  const searchInput = useSearchInput();
  const search = searchInput.debounced;
  const [departmentFilter, setDepartmentFilter] = useState<number | undefined>();
  const [statusFilter, setStatusFilter] = useState<EmployeeStatus | undefined>();
  const [paging, setPaging] = useState({ page: 1, limit: 50 });
  const page = paging.page;
  const [employees, setEmployees] = useState<Employee[]>([]);
  const [total, setTotal] = useState(0);
  const [canViewSensitive, setCanViewSensitive] = useState(false);
  const [loading, setLoading] = useState(false);

  const [departments, setDepartments] = useState<Department[]>([]);

  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState<Employee | null>(null);
  const [saving, setSaving] = useState(false);

  const staffRemote = useRemoteOptions(fetchStaffOptions, modalOpen);

  const [importOpen, setImportOpen] = useState(false);

  const isAdmin = !!userStore.info.role?.isAdmin;
  const canCreate = can(employeePath, "CREATE");
  const canUpdate = can(employeePath, "UPDATE");
  const canDelete = can(employeePath, "DELETE");

  useEffect(() => {
    document.title = getTitle(title);
  }, [title]);

  useEffect(() => {
    departmentApi.findAll().then((res) => setDepartments(res.data.departments || []));
  }, []);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const res = await employeeApi.findAll({
        page,
        limit: paging.limit,
        search,
        departmentId: departmentFilter,
        status: statusFilter,
      });
      setEmployees(res.data.employees || []);
      setTotal(res.data.total || 0);
      setCanViewSensitive(!!res.data.canViewSensitive);
    } finally {
      setLoading(false);
    }
  }, [page, paging.limit, search, departmentFilter, statusFilter]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  useEffect(() => {
    setPaging((prev) => ({ ...prev, page: 1 }));
  }, [search]);

  const { showingFrom, showingTo } = getTableShowingRange(paging.page, paging.limit, total);

  const departmentOptions = useMemo(
    () => departments.map((department) => ({ value: department.id, label: department.name })),
    [departments],
  );

  // Giữ tài khoản CRM đang liên kết trong danh sách chọn khi sửa
  const staffOptions = useMemo<RemoteOption[]>(() => {
    const linked = editing?.staff;
    if (linked && !staffRemote.options.some((option) => option.value === linked.id)) {
      return [{ value: linked.id, label: linked.name || linked.username }, ...staffRemote.options];
    }
    return staffRemote.options;
  }, [editing, staffRemote.options]);

  const importEmployeeFile = useCallback(async (file: File) => {
    const res = await employeeApi.importExcel(file);
    return (res.data.results || []) as ImportExcelResult[];
  }, []);

  const openCreate = () => {
    form.resetFields();
    form.setFieldsValue({ status: "PROBATION" });
    setEditing(null);
    setModalOpen(true);
  };

  const openEdit = useCallback(
    (employee: Employee) => {
      form.resetFields();
      form.setFieldsValue({
        name: employee.name,
        phone: employee.phone,
        email: employee.email,
        departmentId: employee.department?.id,
        staffId: employee.staff?.id,
        status: employee.status,
        technicalCommissionRole: employee.technicalCommissionRole || "",
        probationStartDate: toDate(employee.probationStartDate),
        officialDate: toDate(employee.officialDate),
        resignedDate: toDate(employee.resignedDate),
        lastReviewDate: toDate(employee.lastReviewDate),
        nextReviewDate: toDate(employee.nextReviewDate),
        baseSalary: employee.baseSalary,
        lastReviewSalary: employee.lastReviewSalary,
        idNumber: employee.idNumber,
        bankName: employee.bankName,
        bankAccount: employee.bankAccount,
      });
      setEditing(employee);
      setModalOpen(true);
    },
    [form],
  );

  const handleSubmit = async () => {
    const values = await form.validateFields();

    if (values.probationStartDate && values.officialDate && values.officialDate.isBefore(values.probationStartDate, "day")) {
      message.error("Ngày trở thành nhân viên chính thức không được sớm hơn ngày vào thử việc");
      return;
    }

    const data = {
      employee: {
        name: values.name.trim(),
        phone: values.phone || "",
        email: values.email || "",
        status: values.status,
        technicalCommissionRole: values.technicalCommissionRole || "",
        probationStartDate: fromDate(values.probationStartDate),
        officialDate: fromDate(values.officialDate),
        resignedDate: values.status === "RESIGNED" ? fromDate(values.resignedDate) : "",
        lastReviewDate: fromDate(values.lastReviewDate),
        nextReviewDate: fromDate(values.nextReviewDate),
        ...(canViewSensitive
          ? {
              idNumber: values.idNumber || "",
              baseSalary: Math.round(Number(values.baseSalary) || 0),
              lastReviewSalary: Math.round(Number(values.lastReviewSalary) || 0),
              bankName: values.bankName || "",
              bankAccount: values.bankAccount || "",
            }
          : {}),
      },
      departmentId: values.departmentId || null,
      staffId: values.staffId || null,
    };

    setSaving(true);
    try {
      if (editing) {
        await employeeApi.update(editing.id, data);
      } else {
        await employeeApi.create(data);
      }
      message.success(editing ? "Cập nhật thành công!" : "Tạo mới thành công!");
      setModalOpen(false);
      fetchData();
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = useCallback(
    async (employee: Employee) => {
      await employeeApi.delete(employee.id);
      message.success("Đã xóa hồ sơ nhân sự");
      fetchData();
    },
    [fetchData],
  );

  const columns = useMemo<TableColumnsType<Employee>>(
    () => [
      { title: "Mã", dataIndex: "code", key: "code", width: 100, render: (value) => <TableContent>{value || "--"}</TableContent> },
      {
        title: "Họ tên",
        key: "name",
        render: (_, record) => (
          <div style={{ maxWidth: 360 }}>
            <div className="font-semibold truncate" title={record.name}>{record.name}</div>
            <div className="text-xs text-gray-400 truncate">{[record.phone, record.email].filter(Boolean).join(" · ") || "--"}</div>
          </div>
        ),
      },
      { title: "Phòng ban", key: "department", render: (_, record) => <TableContent>{record.department?.name || "--"}</TableContent> },
      { title: "Trạng thái", dataIndex: "status", key: "status", render: (value) => <StatusCell value={value} trans={employeeStatusTrans} /> },
      { title: "Vào thử việc", dataIndex: "probationStartDate", key: "probationStartDate", render: (value) => displayDate(value) },
      { title: "Chính thức", dataIndex: "officialDate", key: "officialDate", render: (value) => displayDate(value) },
      { title: "Review kỳ tới", dataIndex: "nextReviewDate", key: "nextReviewDate", render: (value) => displayDate(value) },
      {
        title: "Tài khoản CRM",
        key: "staff",
        render: (_, record) => <TableContent>{record.staff ? record.staff.name || record.staff.username : "--"}</TableContent>,
      },
      ...(canViewSensitive
        ? [
            {
              title: "Lương cơ bản",
              dataIndex: "baseSalary",
              key: "baseSalary",
              align: "right" as const,
              render: (value: number) => formatMoney(value),
            },
          ]
        : []),
      ...(canUpdate || canDelete
        ? [
            {
              title: "",
              key: "actions",
              width: 90,
              render: (_: unknown, record: Employee) => (
                <div className="flex items-center gap-1">
                  {canUpdate && (
                    <AppButton tone="light" aria-label="Sửa" onClick={() => openEdit(record)} style={{ padding: "2px 6px" }}>
                      <EditOutlined />
                    </AppButton>
                  )}
                  {canDelete && (
                    <Popconfirm title="Xóa hồ sơ nhân sự này?" okText="Xóa" cancelText="Hủy" onConfirm={() => handleDelete(record)}>
                      <AppButton tone="light" aria-label="Xóa" style={{ padding: "2px 6px" }}>
                        <DeleteOutlined style={{ color: "#ef4444" }} />
                      </AppButton>
                    </Popconfirm>
                  )}
                </div>
              ),
            },
          ]
        : []),
    ],
    [canViewSensitive, canUpdate, canDelete, openEdit, handleDelete],
  );

  return (
    <div className="flex flex-col gap-4">
      <TableLayout
        filter={
          <AppTableToolbar
            search={{
              value: searchInput.value,
              width: 360,
              placeholder: "Tìm theo mã, tên, SĐT",
              onChange: searchInput.onChange,
              inputProps: { allowClear: { clearIcon: <CloseCircleFilled onClick={searchInput.clear} /> } },
            }}
            leftActions={
              <>
                {canCreate && (
                  <AppButton tone="primary" icon={<PlusOutlined />} onClick={openCreate}>
                    Thêm nhân sự
                  </AppButton>
                )}
                {isAdmin && (
                  <AppButton icon={<UploadOutlined />} onClick={() => setImportOpen(true)}>
                    Import Excel
                  </AppButton>
                )}
              </>
            }
            filterLabel={null}
            filters={[
              {
                key: "department",
                value: departmentFilter ?? "",
                options: departmentOptions,
                onChange: (value) => {
                  setPaging((prev) => ({ ...prev, page: 1 }));
                  setDepartmentFilter((value as number) || undefined);
                },
                placeholder: "Phòng ban",
                allowClear: true,
                width: 200,
              },
              {
                key: "status",
                value: statusFilter ?? "",
                options: employeeStatusOptions,
                onChange: (value) => {
                  setPaging((prev) => ({ ...prev, page: 1 }));
                  setStatusFilter((value as EmployeeStatus) || undefined);
                },
                placeholder: "Trạng thái",
                allowClear: true,
                width: 200,
              },
            ]}
          />
        }
        table={<AppTable<Employee> rowKey="id" loading={loading} dataSource={employees} columns={columns} pagination={false} />}
        footer={
          <TableLayoutFooter
            showingFrom={showingFrom}
            showingTo={showingTo}
            total={total}
            currentPage={paging.page}
            pageSize={paging.limit}
            onChange={setPaging}
          />
        }
      />

      <ImportExcelModal
        open={importOpen}
        title="Import nhân sự"
        sampleUrl="/templates/crm_employee.xlsx"
        sampleFileName="crm_employee.xlsx"
        onClose={() => setImportOpen(false)}
        onSuccess={fetchData}
        importFile={importEmployeeFile}
      />

      <Modal
        open={modalOpen}
        title={editing ? `Cập nhật nhân sự ${editing.code}` : "Thêm nhân sự"}
        okText="Lưu"
        cancelText="Hủy"
        confirmLoading={saving}
        onCancel={() => setModalOpen(false)}
        onOk={handleSubmit}
        width={1200}
        style={{ top: 20 }}
        styles={{ body: { maxHeight: "calc(100vh - 190px)", overflowY: "auto" } }}
        destroyOnClose
      >
        <Form
          className="employeeForm"
          layout="horizontal"
          labelAlign="left"
          labelCol={{ flex: "150px" }}
          colon={false}
          form={form}
        >
          <div className="employeeForm__columns">
            <div className="employeeForm__column">
              <section className="employeeForm__section">
                <div className="employeeForm__title">Thông tin cá nhân</div>
                <Form.Item label="Họ tên" name="name" rules={requiredRules}>
                  <Input />
                </Form.Item>
                <Form.Item
                  label="Số điện thoại"
                  name="phone"
                  rules={[{ pattern: /^[0-9+\s.-]{8,15}$/, message: "Số điện thoại không hợp lệ" }]}
                >
                  <Input />
                </Form.Item>
                <Form.Item label="Email" name="email" rules={[{ type: "email", message: "Email không hợp lệ" }]}>
                  <Input />
                </Form.Item>
                {canViewSensitive && (
                  <Form.Item label="CCCD" name="idNumber" rules={[{ pattern: /^\d{12}$/, message: "CCCD gồm đúng 12 chữ số" }]}>
                    <Input maxLength={12} />
                  </Form.Item>
                )}
              </section>

              <section className="employeeForm__section">
                <div className="employeeForm__title">Công việc</div>
                <Form.Item label="Phòng ban" name="departmentId">
                  <Select allowClear placeholder="Chọn phòng ban" options={departmentOptions} />
                </Form.Item>
                <Form.Item label="Trạng thái" name="status" rules={requiredRules}>
                  <Select options={employeeStatusOptions} />
                </Form.Item>
                <Form.Item label="Ngày vào thử việc" name="probationStartDate">
                  <DatePicker style={{ width: "100%" }} format="DD/MM/YYYY" />
                </Form.Item>
                <Form.Item label="Ngày chính thức" name="officialDate">
                  <DatePicker style={{ width: "100%" }} format="DD/MM/YYYY" />
                </Form.Item>
                {statusValue === "RESIGNED" && (
                  <Form.Item label="Ngày nghỉ việc" name="resignedDate" rules={requiredRules}>
                    <DatePicker style={{ width: "100%" }} format="DD/MM/YYYY" />
                  </Form.Item>
                )}
                <Form.Item
                  label="Hoa hồng kỹ thuật"
                  name="technicalCommissionRole"
                  tooltip="Người nhận HH Chương hoặc HH Dung. Khi chốt lương tháng, hoa hồng kỹ thuật chưa trả được cộng vào lương người này."
                >
                  <Select options={technicalCommissionRoleOptions} />
                </Form.Item>
                <Form.Item label="Tài khoản CRM" name="staffId">
                  <Select
                    allowClear
                    showSearch
                    filterOption={false}
                    placeholder="Liên kết nếu là nhân viên sale"
                    options={staffOptions}
                    onSearch={staffRemote.onSearch}
                  />
                </Form.Item>
              </section>
            </div>

            <div className="employeeForm__column">
              <section className="employeeForm__section">
                <div className="employeeForm__title">Lương và review</div>
                {canViewSensitive && (
                  <>
                    <Form.Item label="Lương cơ bản (đ)" name="baseSalary">
                      <InputNumber />
                    </Form.Item>
                    <Form.Item label="Lương review trước (đ)" name="lastReviewSalary">
                      <InputNumber />
                    </Form.Item>
                  </>
                )}
                <Form.Item label="Ngày review trước" name="lastReviewDate">
                  <DatePicker style={{ width: "100%" }} format="DD/MM/YYYY" />
                </Form.Item>
                <Form.Item label="Ngày review tới" name="nextReviewDate">
                  <DatePicker style={{ width: "100%" }} format="DD/MM/YYYY" />
                </Form.Item>
              </section>

              {canViewSensitive && (
                <section className="employeeForm__section">
                  <div className="employeeForm__title">Ngân hàng</div>
                  <Form.Item label="Tên ngân hàng" name="bankName">
                    <Input />
                  </Form.Item>
                  <Form.Item label="Số tài khoản" name="bankAccount">
                    <Input />
                  </Form.Item>
                </section>
              )}
            </div>
          </div>
        </Form>
      </Modal>
    </div>
  );
});
